import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import InlineDialog from 'components/InlineDialog';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';
import Badge from 'kingpin/atoms/Badge';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import SlideOutModeHeader from '../../components/SlideOutModeHeader';
import ConfirmOverrideDialog from './ConfirmOverrideDialog';

const RowDivider = styled.div`
  width: 1px;
  height: 16px;
  background-color: var(--color-border-primary);
  margin: 0 8px;
`;

export const OverridesBadge = ({
  overridesCount,
  hideDivider,
}: {
  overridesCount: number;
  hideDivider?: boolean;
}) => {
  if (overridesCount === 0) {
    return null;
  }

  return (
    <>
      <Badge badgeType={'Orange'}>
        <Row centerAlign style={{ gap: 8 }}>
          <Icon icon={'info'} />
          <Typography.Body type={'Badge'}>
            {`${overridesCount} Override${overridesCount > 1 ? 's' : ''}`}
          </Typography.Body>
        </Row>
      </Badge>
      {!hideDivider && <RowDivider />}
    </>
  );
};

// Staged removals of persisted overrides, shown beside the override count so a
// pending Clear is called out before Confirm.
export const OverridesRemovedBadge = ({
  removedCount,
}: {
  removedCount: number;
}) => {
  if (removedCount === 0) {
    return null;
  }

  return (
    <Badge badgeType={'Blue'}>
      <Row centerAlign style={{ gap: 8 }}>
        <Icon icon={'warning'} />
        <Typography.Body type={'Badge'}>
          {`${removedCount} Override${removedCount > 1 ? 's' : ''} Removed`}
        </Typography.Body>
      </Row>
    </Badge>
  );
};

/**
 * The override-mode header actions — "Override {driver}'s Bonus", the cumulative
 * override count badge, "Undo & Cancel", and "Confirm Override(s)" opening the
 * ConfirmOverrideDialog. Extracted from BonusOverviewTopBar so the entity-app
 * top bar and the performance-board slideout header present identical controls.
 *
 * `layout`:
 * - 'inline' (default) — title and the badge + buttons sit on one row (wide
 *   entity-app top bar).
 * - 'stacked' — title, then the override badge on its own line, then the
 *   buttons, so nothing squishes in the narrow (440px) slideout header.
 */
const OverrideModeActions = ({
  layout = 'inline',
}: {
  layout?: 'inline' | 'stacked';
}) => {
  const { bonusOverview, exitOverrideMode, isLoadingPreview } =
    useContext(BonusOverviewContext);
  const { isOpen, open, close } = usePopup();

  const stagedOverridesCount = useMemo(() => {
    if (!bonusOverview) {
      return 0;
    }

    const { stagedOverrides } = bonusOverview;
    if (!stagedOverrides) {
      return 0;
    }

    let count = 0;
    if (stagedOverrides.qualifier) {
      count++;
    }

    count += stagedOverrides.criteria.length;
    count += stagedOverrides.rewardOverrides?.length ?? 0;

    return count;
  }, [bonusOverview]);

  const actualOverridesCount = useMemo(() => {
    if (!bonusOverview) {
      return 0;
    }

    const { overrides } = bonusOverview;

    let count = 0;
    if (overrides?.qualifierOverride) {
      count++;
    }

    count += overrides?.criteriaOverrides.length ?? 0;
    count += bonusOverview.actualRewardOutput.rewardOverrides?.length ?? 0;

    return count;
  }, [bonusOverview]);

  const cumulativeOverrideCount = useMemo(() => {
    if (!bonusOverview) {
      return 0;
    }

    let count = actualOverridesCount;
    const { stagedOverrides } = bonusOverview;

    if (!stagedOverrides) {
      return count;
    }

    if (stagedOverrides.qualifier) {
      if (stagedOverrides.qualifier.action === 'Clear') {
        count -= 1;
      } else {
        count += 1;
      }
    }

    stagedOverrides.criteria.forEach((c) => {
      if (c.action === 'Clear') {
        count -= 1;
      } else {
        count += 1;
      }
    });

    (stagedOverrides.rewardOverrides ?? []).forEach((r) => {
      if (r.action === 'Clear') {
        count -= 1;
      } else {
        count += 1;
      }
    });

    return count;
  }, [actualOverridesCount, bonusOverview]);

  const isNoteRequired = useMemo(() => {
    if (!bonusOverview) {
      return false;
    }

    const { stagedOverrides } = bonusOverview;
    if (!stagedOverrides) {
      return false;
    }

    return (
      stagedOverrides.criteria.some((c) => c.action === 'Set') ||
      stagedOverrides?.qualifier?.action === 'Set' ||
      (stagedOverrides.rewardOverrides?.some((r) => r.action === 'Set') ??
        false)
    );
  }, [bonusOverview]);

  if (!bonusOverview) {
    return null;
  }

  const title = (
    <Typography.Header
      type={'H3'}
    >{`Override Bonus: ${bonusOverview.driver}`}</Typography.Header>
  );

  const buttons = (
    <>
      <Button
        size={'Medium'}
        type={'Secondary'}
        label={'Undo & Cancel'}
        onClick={exitOverrideMode}
      />
      <InlineDialog
        content={
          <ConfirmOverrideDialog
            close={close}
            isNoteRequired={isNoteRequired}
          />
        }
        isOpen={isOpen}
        onClose={close}
        hasArrow
      >
        <Button
          size={'Medium'}
          type={'Primary'}
          label={'Confirm Override(s)'}
          onClick={open}
          isDisabled={stagedOverridesCount === 0 || isLoadingPreview}
        />
      </InlineDialog>
    </>
  );

  // Stacked (the slideout): the shared mode header — title, the override
  // badge on its own line, then the buttons.
  if (layout === 'stacked') {
    return (
      <SlideOutModeHeader
        title={'Override Bonus'}
        driverName={bonusOverview.driver}
        status={
          cumulativeOverrideCount > 0 ? (
            <OverridesBadge
              overridesCount={cumulativeOverrideCount}
              hideDivider
            />
          ) : undefined
        }
        actions={buttons}
      />
    );
  }

  return (
    <>
      {title}
      <Row style={{ gap: 8 }} centerAlign>
        <OverridesBadge overridesCount={cumulativeOverrideCount} />
        {buttons}
      </Row>
    </>
  );
};

export default OverrideModeActions;
